import { useAuthContext } from "@niutraan/hooks";
import type {
  CreateHubEnrollmentDto,
  HubEnrollmentDto,
} from "@niutraan/shared-dto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@niutraan/hooks/src/lib/api";
import { useHubEnrollmentStore } from "@/stores/useHubEnrollmentStore";

// Query keys
const QUERY_KEYS = {
  hubEnrollments: "hubEnrollments",
  userEnrollments: "userEnrollments",
};

// Get all hub enrollments (admin only)
export const useHubEnrollments = () => {
  const { user } = useAuthContext();

  return useQuery({
    queryKey: [QUERY_KEYS.hubEnrollments],
    queryFn: async () => {
      try {
        const data =
          await apiClient.get<HubEnrollmentDto[]>(`/hub/enrollments`);
        return data;
      } catch (error) {
        console.error("Error fetching hub enrollments:", error);
        throw error;
      }
    },
    enabled: !!user?.id, // Only run the query if we have a user ID
  });
};

// Get current user's enrollments
export const useUserEnrollments = (userId: string) => {
  const setUserEnrollments = useHubEnrollmentStore(
    (state) => state.setUserEnrollments,
  );

  return useQuery({
    queryKey: [QUERY_KEYS.userEnrollments, userId],
    queryFn: async () => {
      try {
        const data = await apiClient.get<HubEnrollmentDto[]>(
          `/hub/enrollments/user/${userId}`,
        );
        setUserEnrollments(data);
        return data;
      } catch (error) {
        console.error(`Error fetching enrollments for user ${userId}:`, error);
        throw error;
      }
    },
    enabled: !!userId,
  });
};

// Get enrollment by ID
export const useHubEnrollmentById = (id: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.hubEnrollments, id],
    queryFn: async () => {
      try {
        return await apiClient.get<HubEnrollmentDto>(`/hub/enrollments/${id}`);
      } catch (error) {
        console.error(`Error fetching hub enrollment with ID ${id}:`, error);
        throw error;
      }
    },
    enabled: !!id,
  });
};

// Create enrollment (user enrolling in a plan)
export const useCreateHubEnrollment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newEnrollment: CreateHubEnrollmentDto) => {
      // In a real app, you would want to let the user select a location
      // For now, we'll use the default location handling in the backend
      return apiClient.post<HubEnrollmentDto>(
        "/hub/enrollments",
        newEnrollment,
      );
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.userEnrollments] });
    },
    onError: (error: Error) => {
      console.error("Error creating hub enrollment:", error);
    },
  });
};
